import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const NavigationContext = createContext();

// Helper function to get page name from pathname
const getPageFromPath = (pathname) => {
  const path = pathname.split('/')[1] || 'home';
  if (path === '' || path === 'home') return 'home';
  if (path === 'case-study') return 'case-study';
  return path;
};

export function NavigationProvider({ children }) {
  const location = useLocation();
  const [destinationPage, setDestinationPage] = useState(null);
  const [activePage, setActivePage] = useState(() => getPageFromPath(location.pathname));
  const [clickedPage, setClickedPage] = useState(null);
  const [previousPage, setPreviousPage] = useState(null);
  const [isNavigatingToCaseStudy, setIsNavigatingToCaseStudy] = useState(false);
  const navigateRef = useRef(null);

  // Update active page when location changes (e.g., on refresh or direct navigation)
  // But only if we don't have a destination set (which means it's not a programmatic navigation)
  useEffect(() => {
    if (!destinationPage) {
      const currentPage = getPageFromPath(location.pathname);
      if (currentPage !== activePage) {
        setActivePage(currentPage);
      }
    }
  }, [location.pathname, destinationPage, activePage]);

  const setNavigate = (navigate) => {
    navigateRef.current = navigate;
  };

  const setPageDestination = (page, slug = null) => {
    // Store current page as previous page if we're navigating away from case-study
    if (activePage === 'case-study' && page !== 'case-study') {
      setPreviousPage(activePage);
    }
    
    // Set clicked page immediately for nav button state
    setClickedPage(page);
    
    // Check if navigating to case study
    if (page === 'case-study') {
      setIsNavigatingToCaseStudy(true);
    }
    
    // For case studies, always trigger transition (even if same page type but different slug)
    // For other pages, only trigger if different page
    const shouldTriggerTransition = page === 'case-study' || page !== activePage;
    
    if (shouldTriggerTransition) {
      setDestinationPage(page);
      
      // Navigate after setting the destination
      if (navigateRef.current) {
        setTimeout(() => {
          const targetPath = slug ? `/${page}/${slug}` : `/${page === 'home' ? '' : page}`;
          navigateRef.current(targetPath);
        }, 100);
      }
    }
  };

  const clearDestination = () => {
    setDestinationPage(null);
    setClickedPage(null); // Clear clicked page when transition is done
    setIsNavigatingToCaseStudy(false); // Clear case study navigation flag
    // Set the active page to the previous destination
    if (destinationPage) {
      setActivePage(destinationPage);
    }
  };

  return (
    <NavigationContext.Provider value={{ 
      destinationPage, 
      activePage,
      clickedPage,
      previousPage,
      isNavigatingToCaseStudy,
      setPageDestination, 
      clearDestination,
      setNavigate
    }}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigation() {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
} 